import { useEffect } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { getThemeTokens, type ThemeTokens } from "@/lib/content.functions";

export const themeTokensQuery = queryOptions({
  queryKey: ["cms", "theme"],
  queryFn: () => getThemeTokens(),
  staleTime: 60_000,
  refetchInterval: 60_000,
});

function buildCss(tokens: ThemeTokens): string {
  const decls = Object.entries(tokens)
    .filter(([k, v]) => k && typeof v === "string" && v.length > 0)
    .map(([k, v]) => `  --${k}: ${v};`)
    .join("\n");
  return `:root {\n${decls}\n}`;
}

const STYLE_ID = "cms-theme-tokens";

export function ThemeInjector() {
  const { data } = useSuspenseQuery(themeTokensQuery);
  const tokens = data?.tokens ?? {};

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!tokens || Object.keys(tokens).length === 0) return;
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = STYLE_ID;
      el.setAttribute("data-theme", "cms");
      document.head.appendChild(el);
    }
    el.textContent = buildCss(tokens);
  }, [tokens]);

  // SSR: render the <style> inline inside body (HeadContent is fixed at SSR);
  // the effect above takes over on the client and keeps it in <head>.
  if (!tokens || Object.keys(tokens).length === 0) return null;
  return (
    <style
      data-theme="cms"
      id={STYLE_ID}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: buildCss(tokens) }}
    />
  );
}
